import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Loader2Icon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { checkLinkedInConnectionStatus } from '@/hooks/user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useLinkedInPopup } from '@/components/onboarding/LinkedInPopup';

import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

const LinkedIn = () => {
  const navigate = useNavigate();
  const { linkedinFeatureEnabled } = useUserStore(
    useShallow((state) => ({
      linkedinFeatureEnabled: state.linkedinFeatureEnabled,
    }))
  );

  useEffect(() => {
    if (linkedinFeatureEnabled === false) {
      navigate('/settings');
    }
  }, [linkedinFeatureEnabled, navigate]);

  if (!linkedinFeatureEnabled) {
    return null;
  }

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>LinkedIn</h3>
          <p className='text-sm text-muted-foreground'>
            Manage your LinkedIn connection
          </p>
        </div>
      </div>
      <Separator />
      <LinkedInConnection />
    </div>
  );
};

export default LinkedIn;

const LinkedInConnection = () => {
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'connected', 'needs_reconnect', 'not_connected'

  let { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  // Use the same popup hook as OnboardingChannel
  const { openLinkedInPopup, loading: popupLoading } = useLinkedInPopup();

  // Check LinkedIn connection status
  const checkStatus = async () => {
    setLoading(true);
    try {
      await checkLinkedInConnectionStatus();
      setConnectionStatus('connected');
    } catch (error) {
      if (error.response?.status === 422) {
        setConnectionStatus('needs_reconnect');
      } else if (error.response?.status === 404) {
        setConnectionStatus('not_connected');
      } else {
        console.error('Failed to check LinkedIn status:', error);
        setConnectionStatus('not_connected');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check current connection status
    checkStatus();
  }, []);

  const handleConnect = async () => {
    await openLinkedInPopup();
  };

  const handleReconnect = async () => {
    await openLinkedInPopup(true); // Pass true to indicate this is a reconnect
  };

  if (loading || popupLoading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2Icon className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const name = user?.customer?.linkedin_name || '';
  const profilePic = user?.customer?.linkedin_profile_image_url || '';

  return (
    <div className='flex items-center justify-center pt-48'>
      {connectionStatus === 'connected' && user?.customer?.linkedin_name ? (
        <div className='flex flex-col items-center gap-4 text-center'>
          <Avatar className='h-24 w-24'>
            <AvatarImage src={profilePic} alt={name} />
            <AvatarFallback>{name?.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className='flex flex-col gap-1'>
            <h2 className='text-2xl font-bold'>{name}</h2>
            <span className='text-sm text-muted-foreground'>
              @{user?.customer?.linkedin_public_id}
            </span>
          </div>
        </div>
      ) : connectionStatus === 'needs_reconnect' ? (
        <div className='flex flex-col items-center'>
          <span>Your LinkedIn connection needs to be reconnected</span>
          <div className='mt-5'>
            <Button
              onClick={handleReconnect}
              className='flex items-center justify-center'
            >
              Reconnect LinkedIn
            </Button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center'>
          <span>You have not connected a LinkedIn Account</span>
          <div className='mt-5'>
            <Button
              onClick={handleConnect}
              disabled={popupLoading}
              className='flex items-center justify-center'
            >
              {popupLoading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Connecting...
                </>
              ) : (
                'Connect LinkedIn'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
